import React from 'react';

/**
 * 描述词圆形角标 —— 全站统一视觉
 *
 * 出现在三处，语义有两种，别搞混：
 *   ① 画布资产节点左下角（CustomNode）        → 点击「编辑」描述词
 *   ② 资料库卡片（AssetDrawer）               → 点击「编辑」描述词
 *   ③ 下游节点控制台的参考图/视频卡片（NodeConsole）→ 点击「开/关」描述词是否进提示词
 *
 * 三处尺寸必须不同，因为承载它的容器差着三倍：
 *   节点 24px / 资料库卡片 18px（与同排删除按钮齐） / 控制台 60px 缩略图上 16px（与关闭按钮齐）
 * 统一的是造型：琥珀色圆形 + 文档图标 + blur + hover 放大，
 * 与多图层模式那颗蓝色圆按钮（ImageNodeView.js）同一套语言，靠颜色区分功能。
 *
 * active=false 时半透明，表示「没有描述词」或「描述词已关闭」。
 */

// 琥珀色：与左侧工具栏剧本大师激活时的 #fbbf24 同色系，
// 也与多图层模式那颗蓝色圆按钮区分开
const AMBER = '245, 158, 11';
// 关闭态：中性灰半透明，不带任何色相 —— 一眼能看出「这条描述词没生效」
const GRAY = '100, 116, 139';

export const descBadgeBg = (active) =>
  active ? `rgba(${AMBER}, 0.95)` : `rgba(${GRAY}, 0.45)`;

/**
 * 无衬线 T 字 —— 取自用户给的「圆圈里一个 T」图标
 *
 * ⚠️ 只画 T，不画那圈圆：按钮容器本身就是实心圆（descBadgeStyle），
 *    图标里再套一圈圆轮廓会变成同心双环，16px 尺寸上糊成一团。
 *    圆由容器提供，T 由这里提供，合起来就是那个图标。
 *
 * 也没用 lucide 的 Type：那个是带上下衬线的排版 T，比这个碎。
 */
export function DescriptionIcon({ size = 14, color = 'white' }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
      style={{ display: 'block' }}
    >
      {/* 横 */}
      <path d="M5.5 6h13" />
      {/* 竖 */}
      <path d="M12 6v12" />
    </svg>
  );
}

/**
 * 圆形按钮容器样式
 * @param {number} size   直径（px）
 * @param {boolean} active 是否高亮（有描述词 / 描述词已开启）
 */
export const descBadgeStyle = (size, active) => ({
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  width: size, height: size,
  borderRadius: '50%',
  background: descBadgeBg(active),
  backdropFilter: 'blur(4px)',
  boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
  cursor: 'pointer',
  transition: 'all 0.2s',
  boxSizing: 'border-box',
});

/**
 * hover 放大 + 加深；配合 descBadgeStyle 使用
 * ⚠️ hover 要按 active 分色：关闭态 hover 也变琥珀会让人以为已经点开了。
 *    所以关闭态 hover 只是灰色加深。
 */
export const descBadgeHover = {
  onMouseEnter: (active) => (e) => {
    e.currentTarget.style.transform = 'scale(1.1)';
    e.currentTarget.style.background = active
      ? `rgba(${AMBER}, 1)`
      : `rgba(${GRAY}, 0.75)`;
  },
  onMouseLeave: (active) => (e) => {
    e.currentTarget.style.transform = 'scale(1)';
    e.currentTarget.style.background = descBadgeBg(active);
  },
};
